import React from 'react';
import { Button } from '../components/ui/button';
import { X, User, Car, CreditCard, ShieldAlert, Phone, Banknote, FileText } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value || <span className="text-gray-400">—</span>}</p>
  </div>
);

const DocThumb = ({ label, driverId, docType, token, hasDoc }) => {
  if (!hasDoc) {
    return (
      <div className="flex flex-col items-center justify-center h-28 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-gray-400 text-xs">
        <FileText className="w-5 h-5 mb-1" />
        {label}
        <span className="text-[10px]">Not uploaded</span>
      </div>
    );
  }
  const src = `${BACKEND}/api/drivers/${driverId}/photo/${docType}?auth=${token}`;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block group" data-testid={`doc-${docType}`}>
      <div className="h-28 rounded-lg border overflow-hidden bg-gray-100">
        <img src={src} alt={label} className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" />
      </div>
      <p className="text-xs text-center text-gray-600 mt-1">{label}</p>
    </a>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
      <Icon className="w-4 h-4 text-indigo-600" /> {title}
    </h4>
    {children}
  </div>
);

export const DriverDetailsModal = ({ driver, token, onClose, onApprove, onReject }) => {
  if (!driver) return null;
  const d = driver;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="driver-details-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-indigo-100 flex items-center justify-center">
              {d.avatar_url ? (
                <img src={d.avatar_url.startsWith('http') ? d.avatar_url : `${BACKEND}${d.avatar_url}`} alt={d.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-indigo-500" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{d.name}</h3>
              <p className="text-sm text-gray-500">{d.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-testid="close-driver-details">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <Section icon={User} title="Personal">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Mobile" value={d.phone} />
              <Field label="Date of birth" value={d.date_of_birth} />
              <Field label="City" value={d.city} />
            </div>
          </Section>

          <Section icon={Car} title="Vehicle">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Type" value={d.vehicle_type} />
              <Field label="Make & model" value={d.vehicle_make_model} />
              <Field label="Colour" value={d.vehicle_colour} />
              <Field label="Registration plate" value={d.vehicle_plate} />
            </div>
          </Section>

          <Section icon={CreditCard} title="Licence & Compliance">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Licence number" value={d.licence_number} />
              <Field label="State" value={d.licence_state} />
              <Field label="Expiry" value={d.licence_expiry} />
            </div>
          </Section>

          <Section icon={Banknote} title="Payment & Emergency Contact">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="BSB" value={d.bank_bsb} />
              <Field label="Account" value={d.bank_account} />
              <Field label="ABN" value={d.abn} />
              <Field label="Emergency name" value={d.emergency_contact_name} />
              <Field label="Emergency phone" value={d.emergency_contact_phone} />
            </div>
            {!d.abn && (
              <div className="flex items-center gap-2 mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <ShieldAlert className="w-4 h-4 shrink-0" /> No ABN on file — flag for tax implications.
              </div>
            )}
          </Section>

          <Section icon={FileText} title="Documents">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <DocThumb label="Profile" driverId={d.id} docType="profile" token={token} hasDoc={!!d.profile_photo_path} />
              <DocThumb label="Licence front" driverId={d.id} docType="licence_front" token={token} hasDoc={!!d.licence_front_path} />
              <DocThumb label="Licence back" driverId={d.id} docType="licence_back" token={token} hasDoc={!!d.licence_back_path} />
              <DocThumb label="Insurance" driverId={d.id} docType="insurance" token={token} hasDoc={!!d.insurance_proof_path} />
            </div>
          </Section>

          <div className="flex items-center justify-between pt-2 border-t">
            <div className="text-sm">
              <span className="text-gray-500">Onboarding: </span>
              <span className={d.onboarding_complete ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'}>
                {d.onboarding_complete ? 'Complete' : 'Incomplete'}
              </span>
            </div>
            {d.approval_status === 'pending' && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onReject(d.id)}
                  className="border-red-300 text-red-600 hover:bg-red-50" data-testid="modal-reject-driver">
                  Reject
                </Button>
                <Button onClick={() => onApprove(d.id)} className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="modal-approve-driver">
                  Approve Driver
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
